import type { WebMedia } from '../types/media';

type ImageVariant = 'thumb' | 'gallery' | 'display' | 'full' | 'share';

const IMAGE_VARIANTS: Record<ImageVariant, string> = {
  thumb: 'width=420,height=420,fit=cover,quality=75,format=auto',
  gallery: 'width=1600,fit=scale-down,quality=82,format=auto',
  display: 'width=1600,fit=scale-down,quality=82,format=auto',
  full: 'width=2400,fit=scale-down,quality=88,format=auto',
  share: 'width=1080,fit=scale-down,quality=86,format=auto',
};

const SIGNED_URL_PARAM_PATTERN =
  /[?&](x-amz-signature|x-amz-credential|x-amz-expires|x-amz-security-token|awsaccesskeyid|signature|expires|token|sig)=/i;

function cleanText(value: unknown): string {
  return String(value ?? '').trim();
}

function cleanBaseUrl(value: unknown): string {
  return cleanText(value).replace(/\/+$/, '');
}

const publicMediaBaseUrl = cleanBaseUrl(
  import.meta.env.VITE_R2_PUBLIC_BASE_URL || import.meta.env.VITE_MEDIA_CDN_BASE_URL,
);

const imageTransformBaseUrl = cleanBaseUrl(
  import.meta.env.VITE_CLOUDFLARE_IMAGE_TRANSFORM_BASE_URL ||
    import.meta.env.VITE_MEDIA_CDN_BASE_URL ||
    import.meta.env.VITE_R2_PUBLIC_BASE_URL,
);

export function isHttpMediaUrl(value: unknown): value is string {
  return /^https?:\/\//i.test(cleanText(value));
}

function hasLocalOnlyUri(value: unknown): boolean {
  return /^(file|ph|assets-library|content):/i.test(cleanText(value));
}

function normalizeObjectKey(value: unknown): string {
  return cleanText(value).replace(/^\/+/, '');
}

function encodeObjectKey(key: string): string {
  return key
    .split('/')
    .filter(Boolean)
    .map((part) => encodeURIComponent(part))
    .join('/');
}

function buildPublicMediaUrl(objectKey: unknown): string | null {
  const key = normalizeObjectKey(objectKey);
  if (!key) return null;
  if (isHttpMediaUrl(key)) return key;
  if (!publicMediaBaseUrl) return null;
  return `${publicMediaBaseUrl}/${encodeObjectKey(key)}`;
}

function buildCloudflareImageUrlForKey(
  objectKey: unknown,
  variant: ImageVariant,
): string | null {
  const sourceUrl = buildPublicMediaUrl(objectKey);
  if (!sourceUrl) return null;
  if (!imageTransformBaseUrl) return sourceUrl;
  return `${imageTransformBaseUrl}/cdn-cgi/image/${IMAGE_VARIANTS[variant]}/${sourceUrl}`;
}

function firstStableHttpUrl(...values: unknown[]): string | null {
  for (const value of values) {
    const text = cleanText(value);
    if (!isHttpMediaUrl(text)) continue;
    if (SIGNED_URL_PARAM_PATTERN.test(text)) continue;
    return text;
  }
  return null;
}

function resolveImageVariant(media: WebMedia, variant: ImageVariant): string | null {
  const variantKey = normalizeObjectKey(
    variant === 'thumb'
      ? media.thumbnail_storage_path
      : variant === 'display' || variant === 'gallery'
        ? media.display_storage_path
        : variant === 'full'
          ? media.original_storage_path
          : variant === 'share'
            ? media.watermarked_storage_path
            : null,
  );
  const fallbackKey = normalizeObjectKey(
    variantKey ||
      media.r2_key ||
      media.display_storage_path ||
      media.thumbnail_storage_path ||
      media.original_storage_path ||
      media.storage_path,
  );
  return fallbackKey ? buildCloudflareImageUrlForKey(fallbackKey, variant) : null;
}

export function resolvePhotoDelivery(media: WebMedia) {
  if (media.media_type === 'video') {
    return { thumb: null, display: null, full: null, share: null };
  }

  const thumb = firstStableHttpUrl(
    media.thumbnail_url,
    resolveImageVariant(media, 'thumb'),
  );
  const display = firstStableHttpUrl(
    media.display_url,
    media.cloudflare_image_url,
    resolveImageVariant(media, 'display'),
    resolveImageVariant(media, 'gallery'),
  );
  const full = firstStableHttpUrl(
    media.original_url,
    media.display_url,
    resolveImageVariant(media, 'full'),
    display,
  );
  const share = firstStableHttpUrl(
    media.watermarked_url,
    media.display_url,
    resolveImageVariant(media, 'share'),
    display,
  );

  return { thumb, display, full, share };
}

export function isVideoPlaybackReady(media: WebMedia): boolean {
  if (media.media_type !== 'video') return false;
  const streamUid = cleanText(media.cloudflare_stream_uid);
  const streamStatus = cleanText(media.cloudflare_stream_status).toLowerCase();
  const lifecycle = cleanText(media.lifecycle_state).toLowerCase();
  const moderation = cleanText(media.moderation_status).toLowerCase();
  const status = cleanText(media.status).toLowerCase();
  const streamReady =
    ['ready', 'readytostream', 'ready_to_stream'].includes(streamStatus) ||
    Boolean(cleanText(media.cloudflare_stream_ready_at));

  return Boolean(
    streamUid &&
      streamReady &&
      lifecycle === 'published' &&
      moderation === 'approved' &&
      status === 'active',
  );
}

export function resolveStableVideoPoster(media: WebMedia): string | null {
  if (media.media_type !== 'video') return null;
  return firstStableHttpUrl(
    media.thumbnail_url,
    media.cloudflare_stream_thumbnail_url,
  );
}

export function isGalleryMediaEligible(media: WebMedia | null | undefined): boolean {
  if (!media) return false;
  const status = cleanText(media.status || 'active').toLowerCase();
  const lifecycle = cleanText(media.lifecycle_state).toLowerCase();
  const moderation = cleanText(media.moderation_status).toLowerCase();
  const upload = cleanText(media.upload_status).toLowerCase();

  if (['removed', 'deleted', 'hidden'].includes(status)) return false;
  if (['deleted', 'hidden', 'archived', 'failed'].includes(lifecycle)) return false;
  if (['failed', 'cancelled'].includes(upload)) return false;
  if (hasLocalOnlyUri(media.thumbnail_url) || hasLocalOnlyUri(media.display_url)) return false;

  // Mirrors the mobile Apple UGC publication gate. Server RLS remains the
  // authoritative block/access boundary for browser reads.
  return moderation === 'approved';
}

export function attachResolvedPhotoDelivery(media: WebMedia): WebMedia {
  if (media.media_type === 'video') {
    return {
      ...media,
      delivery: {
        thumb: null,
        display: null,
        full: null,
        poster: resolveStableVideoPoster(media),
        playback: null,
      },
    };
  }

  const urls = resolvePhotoDelivery(media);
  return {
    ...media,
    delivery: {
      thumb: urls.thumb,
      display: urls.display,
      full: urls.full,
      poster: null,
      playback: null,
    },
  };
}
